import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import ReactMarkdown from "react-markdown";
import { Sparkles, X, Send, Loader2, RotateCcw } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { API, api } from "@/lib/api";

const SUGGESTIONS = ["Walk me through getting started", "What should I do next?", "How do I become 100% ready to apply?", "How does the human review work?"];

async function streamChat(message, onDelta) {
  await api.get("/auth/me");
  const res = await fetch(`${API}/guide/chat`, { method: "POST", credentials: "include", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ message }) });
  if (!res.ok) throw new Error("The guide is unavailable right now.");
  const reader = res.body.getReader();
  const dec = new TextDecoder();
  let buf = "";
  for (;;) {
    const { value, done } = await reader.read();
    if (done) break;
    buf += dec.decode(value, { stream: true });
    const evs = buf.split("\n\n");
    buf = evs.pop();
    for (const ev of evs) {
      if (!ev.startsWith("data: ")) continue;
      const m = JSON.parse(ev.slice(6));
      if (m.error) throw new Error(m.error);
      if (m.delta) onDelta(m.delta);
    }
  }
}

function Bubble({ m, onLink }) {
  const mine = m.role === "user";
  return (
    <div className={`flex ${mine ? "justify-end" : "justify-start"}`}>
      <div className={`max-w-[85%] rounded-lg px-3.5 py-2.5 text-sm leading-relaxed ${mine ? "bg-slate-900 text-white" : "bg-slate-100 text-slate-800 prose-gv guide-md"}`}>
        {mine ? m.content : (
          <ReactMarkdown components={{ a: ({ href, children }) => href?.startsWith("/") ? (
            <button className="text-emerald-700 font-semibold underline" onClick={() => onLink(href)}>{children}</button>
          ) : <a href={href} target="_blank" rel="noreferrer" className="text-emerald-700 underline">{children}</a> }}>{m.content}</ReactMarkdown>
        )}
      </div>
    </div>
  );
}

export function GuidePanel({ open, setOpen }) {
  const navigate = useNavigate();
  const [msgs, setMsgs] = useState([]);
  const [text, setText] = useState("");
  const [busy, setBusy] = useState(false);
  const endRef = useRef(null);
  useEffect(() => { if (open) api.get("/guide/history").then((r) => setMsgs(r.data)); }, [open]);
  useEffect(() => { endRef.current?.scrollIntoView({ behavior: "smooth" }); }, [msgs]);

  const send = async (message) => {
    if (!message.trim() || busy) return;
    setText("");
    setBusy(true);
    setMsgs((m) => [...m, { role: "user", content: message }, { role: "assistant", content: "" }]);
    try {
      await streamChat(message, (d) => setMsgs((m) => [...m.slice(0, -1), { role: "assistant", content: m[m.length - 1].content + d }]));
    } catch (e) {
      setMsgs((m) => [...m.slice(0, -1), { role: "assistant", content: e.message }]);
    } finally {
      setBusy(false);
    }
  };
  const clear = async () => { await api.delete("/guide/history"); setMsgs([]); };
  const go = (href) => { navigate(href); };

  return (
    <>
      {!open && (
        <button onClick={() => setOpen(true)} data-testid="ai-guide-open-button" aria-label="AI Guide" className="fixed bottom-[5.5rem] right-4 lg:bottom-6 lg:right-6 z-40 flex items-center justify-center gap-2 bg-emerald-600 hover:bg-emerald-700 text-white rounded-full w-12 sm:w-auto sm:pl-4 sm:pr-5 h-12 shadow-lg transition-transform duration-200 hover:-translate-y-0.5">
          <Sparkles className="h-5 w-5 sm:h-4 sm:w-4" /><span className="font-semibold text-sm hidden sm:inline">AI Guide</span>
        </button>
      )}
      {open && (
        <div className="fixed bottom-0 right-0 md:bottom-6 md:right-6 z-[45] w-full md:w-[400px] h-[85vh] md:h-[600px] bg-white border border-slate-200 md:rounded-lg shadow-2xl flex flex-col fade-up" data-testid="ai-guide-panel">
          <div className="flex items-center justify-between px-4 py-3 border-b border-slate-200 bg-slate-900 text-white md:rounded-t-lg">
            <div className="flex items-center gap-2"><Sparkles className="h-4 w-4 text-emerald-400" /><span className="font-display font-bold">GrantVerify Guide</span></div>
            <div className="flex gap-1">
              <button onClick={clear} title="Start over" className="p-1.5 hover:bg-slate-800 rounded" data-testid="ai-guide-clear-button"><RotateCcw className="h-4 w-4" /></button>
              <button onClick={() => setOpen(false)} className="p-1.5 hover:bg-slate-800 rounded" data-testid="ai-guide-close-button"><X className="h-4 w-4" /></button>
            </div>
          </div>
          <div className="flex-1 overflow-y-auto p-4 space-y-3" data-testid="ai-guide-messages">
            {msgs.length === 0 && (
              <div>
                <p className="text-sm text-slate-600">Hi! I'm your AI guide. I'll walk you through GrantVerify step by step — from finding grants to getting your proposal certified.</p>
                <div className="mt-4 flex flex-col gap-2">
                  {SUGGESTIONS.map((s, i) => <button key={s} onClick={() => send(s)} data-testid={`ai-guide-suggestion-${i}`} className="text-left text-sm border border-slate-200 rounded-md px-3 py-2 hover:border-emerald-400 hover:bg-emerald-50 transition-colors">{s}</button>)}
                </div>
              </div>
            )}
            {msgs.map((m, i) => <Bubble key={i} m={m} onLink={go} />)}
            {busy && msgs[msgs.length - 1]?.content === "" && <Loader2 className="h-4 w-4 animate-spin text-emerald-600" />}
            <div ref={endRef} />
          </div>
          <form onSubmit={(e) => { e.preventDefault(); send(text); }} className="p-3 border-t border-slate-200 flex gap-2">
            <Input value={text} onChange={(e) => setText(e.target.value)} placeholder="Ask anything about GrantVerify…" data-testid="ai-guide-input" />
            <Button type="submit" disabled={busy || !text.trim()} className="bg-emerald-600 hover:bg-emerald-700" data-testid="ai-guide-send-button"><Send className="h-4 w-4" /></Button>
          </form>
        </div>
      )}
    </>
  );
}
